import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../../utils/logger';
import { 
  X, 
  Plus, 
  MessageSquare, 
  Search, 
  Trash2, 
  Edit3, 
  Clock, 
  Star,
  Archive,
  Filter,
  MoreVertical,
  Calendar,
  Users,
  TrendingUp
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { supabase } from '../../config/supabase';

const ChatSidebar = ({ onClose, currentConversation, conversations, onNewConversation, onSelectConversation, onDeleteConversation }) => {
  const { currentUser } = useAuth();
  const { leads, meetings, getDashboardStats } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('conversations'); // conversations, context, insights
  const [showContextMenu, setShowContextMenu] = useState(null);

  // Handle new conversation creation
  const handleNewConversation = async () => {
    try {
      if (onNewConversation) {
        await onNewConversation();
        logger.log('✅ New conversation created');
      }
    } catch (error) {
      logger.error('Error creating conversation:', error);
    }
  };
  
  // Handle conversation selection
  const handleSelectConversation = async (conversation) => {
    try {
      if (onSelectConversation) {
        await onSelectConversation(conversation);
        logger.log(`✅ Switched to conversation: ${conversation.title}`);
      }
    } catch (error) {
      logger.error('Error selecting conversation:', error);
    }
  };

  // Delete conversation
  const handleDeleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;
    
    try {
      // Use the parent's delete handler to ensure proper cascade deletion
      if (onDeleteConversation) {
        await onDeleteConversation(conversationId);
      }
    } catch (error) {
      logger.error('Error deleting conversation:', error);
    }
  };

  // Update conversation title
  const handleUpdateTitle = async (conversationId, newTitle) => {
    try {
      const { error } = await supabase
        .from('chat_conversations')
        .update({ title: newTitle })
        .eq('id', conversationId);

      if (error) throw error;
      
      // Note: Parent component manages conversations state
      // This update will be reflected when conversations are reloaded
      logger.log(`✅ Updated conversation title: ${newTitle}`);
      
      // Optionally notify parent component about the title change
      // For now, the change will be visible on next conversation load
    } catch (error) {
      logger.error('Error updating conversation title:', error);
    }
  };

  // Filter conversations based on search
  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format time for display
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) {
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get current context for display
  const getCurrentContext = () => {
    const stats = getDashboardStats();
    return {
      totalLeads: stats.totalLeads,
      callListLeads: stats.callListLeads,
      upcomingMeetings: meetings.filter(m => new Date(m.meeting_date) >= new Date()).length,
      recentActivity: {
        newLeads: leads.filter(l => {
          const created = new Date(l.created_at);
          const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
          return created > dayAgo;
        }).length,
        replies: stats.replies,
        opens: stats.opens
      }
    };
  };

  const context = getCurrentContext();

  return (
    <motion.div
      initial={{ x: -300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -300, opacity: 0 }}
      className="w-80 bg-white border-r border-gray-200 flex flex-col h-full"
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Chat Assistant</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
          {[
            { id: 'conversations', label: 'Chats', icon: MessageSquare },
            { id: 'context', label: 'Context', icon: Users },
            { id: 'insights', label: 'Insights', icon: TrendingUp }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center space-x-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'conversations' && (
          <div className="h-full flex flex-col">
            {/* Search and New Chat */}
            <div className="p-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <button
                onClick={handleNewConversation}
                className="w-full flex items-center justify-center space-x-2 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>New Chat</span>
              </button>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">
                    {searchTerm ? 'No conversations found' : 'No conversations yet'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1 px-2">
                  {filteredConversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      className={`relative group p-3 rounded-lg cursor-pointer transition-colors ${
                        currentConversation?.id === conversation.id
                          ? 'bg-blue-50 border border-blue-200'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleSelectConversation(conversation)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">
                            {conversation.title}
                          </h3>
                          <p className="text-sm text-gray-500 truncate mt-1">
                            {conversation.lastMessage}
                          </p>
                          <div className="flex items-center space-x-2 mt-2">
                            <Clock className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-400">
                              {formatTime(conversation.lastMessageTime)}
                            </span>
                            {conversation.messageCount > 0 && (
                              <span className="text-xs text-gray-400">
                                • {conversation.messageCount} messages
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {conversation.unreadCount > 0 && (
                          <div className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {conversation.unreadCount}
                          </div>
                        )}
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowContextMenu(showContextMenu === conversation.id ? null : conversation.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 transition-all"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Context Menu */}
                      <AnimatePresence>
                        {showContextMenu === conversation.id && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="absolute right-2 top-12 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10"
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const newTitle = prompt('Enter new title:', conversation.title);
                                if (newTitle) handleUpdateTitle(conversation.id, newTitle);
                                setShowContextMenu(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                            >
                              <Edit3 className="w-4 h-4" />
                              <span>Rename</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteConversation(conversation.id);
                                setShowContextMenu(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete</span>
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'context' && (
          <div className="p-4 space-y-4">
            <h3 className="font-medium text-gray-900">Current Context</h3>
            
            <div className="space-y-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">CRM Overview</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Leads:</span>
                    <span className="font-medium">{context.totalLeads}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Call List:</span>
                    <span className="font-medium">{context.callListLeads}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Upcoming Meetings:</span>
                    <span className="font-medium">{context.upcomingMeetings}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Activity (24h)</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">New Leads:</span>
                    <span className="font-medium">{context.recentActivity.newLeads}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email Replies:</span>
                    <span className="font-medium">{context.recentActivity.replies}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email Opens:</span>
                    <span className="font-medium">{context.recentActivity.opens}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="p-4 space-y-4">
            <h3 className="font-medium text-gray-900">AI Insights</h3>
            
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <h4 className="text-sm font-medium text-blue-800 mb-1">💡 Suggestion</h4>
                <p className="text-sm text-blue-700">
                  You have {context.callListLeads} leads ready for calls. Consider scheduling follow-ups.
                </p>
              </div>
              
              {context.recentActivity.newLeads > 0 && (
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <h4 className="text-sm font-medium text-green-800 mb-1">🎯 Opportunity</h4>
                  <p className="text-sm text-green-700">
                    {context.recentActivity.newLeads} new leads today. Review and prioritize them.
                  </p>
                </div>
              )}
              
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-1">📊 Quick Stats</h4>
                <p className="text-sm text-gray-600">
                  Conversion rate: {context.totalLeads > 0 ? Math.round((context.callListLeads / context.totalLeads) * 100) : 0}%
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ChatSidebar;